import {useEffect, useMemo, useRef, useState} from 'react';
import {APIClient} from '../../../utils/APIClient';
import {
    eventDependentFieldsOf, eventsCheckUrl, templateUsesEventMetrics, unconfiguredInScope,
} from './eventsCheck';

const NONE = [];

/**
 * Las cuentas del informe sin eventos de conversión elegidos (ver `eventsCheck.js`).
 *
 * Sólo pregunta si algún bloque muestra métricas de eventos, y vuelve a preguntar cuando cambia el
 * cliente o las líneas de control elegidas — que es «al elegir una cuenta», lo que pide la card.
 * Las cuentas elegidas NO disparan un pedido: se recortan acá sobre la respuesta que ya está.
 *
 * Si el chequeo falla, no avisa nada: es un aviso de ayuda, y afirmar un problema que no se pudo
 * comprobar sería peor que callar.
 *
 * @returns `{unconfigured, recheck}`. `recheck()` se llama después de elegir eventos.
 */
export function useEventsCheck({agencyClientId, settings, template, fieldsData, adAccounts, businessUnits}) {
    const eventFields = eventDependentFieldsOf(fieldsData);
    const active = !!agencyClientId && templateUsesEventMetrics(template, eventFields);
    const url = active ? eventsCheckUrl(agencyClientId, settings, businessUnits) : null;
    const [response, setResponse] = useState({url: null, accounts: NONE});
    const [nonce, setNonce] = useState(0);
    const requestId = useRef(0);

    useEffect(() => {
        requestId.current += 1;
        const id = requestId.current;
        if (!url) {
            setResponse({url: null, accounts: NONE});
            return;
        }
        const settle = (accounts) => {
            if (requestId.current === id) setResponse({url, accounts});
        };
        new APIClient(url).call('GET')
            .then((r) => settle(r && r.status === 'OK' && r.data ? (r.data.unconfigured_accounts || NONE) : NONE))
            .catch(() => settle(NONE));
    }, [url, nonce]);

    const sourceType = settings && settings.source_type;
    const selectedItems = settings && settings.selected_items;
    const unconfigured = useMemo(() => {
        // Una respuesta de OTRO cliente o alcance no se muestra mientras llega la nueva.
        if (!url || response.url !== url) return NONE;
        return unconfiguredInScope(response.accounts, {source_type: sourceType, selected_items: selectedItems}, adAccounts);
    }, [url, response, sourceType, selectedItems, adAccounts]);

    return {unconfigured, recheck: () => setNonce((n) => n + 1)};
}
